import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useActiveProperty } from "@/context/ActivePropertyContext";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { BulkGrid } from "@/components/BulkGrid";
import { ApDocuments } from "@/components/ApDocuments";
import { AutopayDialog } from "@/components/AutopayDialog";
import { Plus, Loader2, Trash2, Pencil, FileText, Eye, Printer, Building, Receipt, Upload, Settings, ClipboardPaste, CheckCircle2, Download, ChevronLeft, ChevronRight, CreditCard, Undo2, ArrowUp, ArrowDown, ArrowUpDown, Send } from "lucide-react";
import { VendorPortalFields, VendorPortalCell } from "@/components/VendorPortal";
import { InvoiceFileUpload } from "@/components/InvoiceFileUpload";
import { SendInvoiceApproval, OwnerApprovalBadge } from "@/components/SendInvoiceApproval";
import { InvoiceHoverPreview } from "@/components/InvoiceHoverPreview";
import { useBankAccounts, BankAccountSelect, saveBankAccounts } from "@/components/BankAccounts";

const EMPTY_VENDOR = { name: "", contact_name: "", phone: "", email: "", address: "", city: "", state: "", zip: "", account_number: "", notes: "", portal_url: "", portal_username: "", portal_notes: "" };
const STATUS_ORDER = { pending: 0, rejected: 1, approved: 2, scheduled: 3, paid: 4, settled: 5 };
const sortInvoices = (list, { key, dir }) => {
  if (!key) return list;
  const val = (i) => (key === "date" ? i.invoice_date || "" : key === "vendor" ? (i.vendor_name || "").toLowerCase() : STATUS_ORDER[i.status] ?? 9);
  const m = dir === "desc" ? -1 : 1;
  return [...list].sort((a, b) => { const x = val(a), y = val(b); return (x < y ? -1 : x > y ? 1 : 0) * m; });
};

const EMPTY_INVOICE = { vendor_id: "", invoice_date: "", amount: "", invoice_number: "", memo: "", autopay: false, autopay_date: "" };
const EMPTY_BANK = { company_name: "", bank_name: "", routing_number: "", account_number: "", current_check_number: 1001, check_stock_type: "blank" };
const EMPTY_RECEIPT = { vendor_id: "", date: "", amount: "", category: "", memo: "", invoice_id: "", card_id: "" };

function money(v) {
  return `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
const STATUS_STYLE = {
  pending: "bg-[hsl(var(--warning))] text-black",
  approved: "bg-[hsl(var(--success))] text-white",
  paid: "bg-primary text-primary-foreground",
  rejected: "bg-destructive text-destructive-foreground",
  scheduled: "bg-sky-600 text-white",
};

const STATUS_LABEL = { rejected: "Unapproved", scheduled: "Scheduled Autopay" };

export default function Invoices() {
  const { user } = useAuth();
  const [autopayInv, setAutopayInv] = useState(null);
  const [exportingPaid, setExportingPaid] = useState(false);
  const canManage = can(user, ["admin", "regional", "finance", "gm"]);
  const canDeleteVendor = can(user, ["admin", "regional", "finance"]);
  const canVendorSecret = can(user, ["admin", "regional", "finance", "accountant", "gm"]);
  const canPrintBlank = user?.role === "admin" || (user?.permissions || []).includes("print_blank_checks");

  const [props, setProps] = useState([]);
  const { activePropertyId: propertyId, setActivePropertyId: setPropertyId } = useActiveProperty();
  const [vendors, setVendors] = useState([]);
  const [invoices, setInvoices] = useState(null);

  // vendor dialog
  const [vOpen, setVOpen] = useState(false);
  const [vEditId, setVEditId] = useState(null);
  const [vForm, setVForm] = useState(EMPTY_VENDOR);
  const [vSaving, setVSaving] = useState(false);

  // bulk vendor dialog
  const [bulkVOpen, setBulkVOpen] = useState(false);
  const [bulkVRows, setBulkVRows] = useState([]);
  const [bulkVSaving, setBulkVSaving] = useState(false);

  // invoice dialog
  const [iOpen, setIOpen] = useState(false);
  const [iForm, setIForm] = useState(EMPTY_INVOICE);
  const [iFile, setIFile] = useState(null);
  const [iSaving, setISaving] = useState(false);

  // viewer
  const [viewer, setViewer] = useState(null); // {url, type, name}
  const [printingId, setPrintingId] = useState(null);
  const [selIds, setSelIds] = useState([]);
  const [downloading, setDownloading] = useState(false);

  // ---------- CC Expenses (receipts + card settlement) ----------
  const [ccData, setCcData] = useState(null); // {receipts, categories, summary, can_pay, can_manage}
  const [ccPayments, setCcPayments] = useState([]);
  const [ccFilter, setCcFilter] = useState("unpaid"); // unpaid | settled | all
  const [rOpen, setROpen] = useState(false);
  const [rEditId, setREditId] = useState(null);
  const [rForm, setRForm] = useState(EMPTY_RECEIPT);
  const [rFile, setRFile] = useState(null);
  const [rSaving, setRSaving] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [payDate, setPayDate] = useState("");
  const [payAmount, setPayAmount] = useState("");
  const [paySel, setPaySel] = useState([]);
  const [payCard, setPayCard] = useState("");
  const [paySaving, setPaySaving] = useState(false);
  // Pay an invoice by credit card (from the invoice row)
  const [ccInvOpen, setCcInvOpen] = useState(false);
  const [ccInv, setCcInv] = useState(null);
  const [ccInvForm, setCcInvForm] = useState({ amount: "", category: "", date: "", memo: "" });
  const [ccInvFile, setCcInvFile] = useState(null);
  const [ccInvSaving, setCcInvSaving] = useState(false);

  // batch check selection
  const canViewDeleted = can(user, ["admin", "finance"]);
  const [batchOpen, setBatchOpen] = useState(false);
  const [pendingChecks, setPendingChecks] = useState([]);
  const [batchSel, setBatchSel] = useState([]);
  const [batching, setBatching] = useState(false);
  // deleted invoices archive
  const [deletedList, setDeletedList] = useState([]);
  const [delCommentText, setDelCommentText] = useState({});

  const monthStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  const fmtDT = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso || ""; } };
  const monthBounds = (ym) => {
    const [y, m] = ym.split("-").map(Number);
    const last = new Date(y, m, 0).getDate();
    return { from: `${ym}-01`, to: `${ym}-${String(last).padStart(2, "0")}` };
  };
  const fmtMonth = (ym) => { const [y, m] = ym.split("-").map(Number); return new Date(y, m - 1, 1).toLocaleString("en-US", { month: "long", year: "numeric" }); };
  const _thisMonth = monthStr(new Date());
  const _initB = monthBounds(_thisMonth);
  const [invSort, setInvSort] = useState({ key: "", dir: "" });
  const [sendApprovalFor, setSendApprovalFor] = useState(null);
  const bankAccts = useBankAccounts(propertyId);
  const [printBankId, setPrintBankId] = useState(null);
  const [blankBankId, setBlankBankId] = useState(null);
  const [settingsBankId, setSettingsBankId] = useState(null);
  const [batchBankIds, setBatchBankIds] = useState({});
  const [invMonth, setInvMonth] = useState(_thisMonth); // "" when a custom range is set
  const [dateFrom, setDateFrom] = useState(_initB.from);
  const [dateTo, setDateTo] = useState(_initB.to);
  const shiftMonth = (delta) => {
    const base = invMonth || _thisMonth;
    const [y, m] = base.split("-").map(Number);
    const ym = monthStr(new Date(y, m - 1 + delta, 1));
    const b = monthBounds(ym); setInvMonth(ym); setDateFrom(b.from); setDateTo(b.to);
  };
  const pickLast3 = () => {
    const [y, m] = _thisMonth.split("-").map(Number);
    setInvMonth(""); setDateFrom(monthBounds(monthStr(new Date(y, m - 3, 1))).from); setDateTo(monthBounds(_thisMonth).to);
  };
  const onTabChange = (t) => { if (t === "paid") pickLast3(); else if (t === "invoices") pickThisMonth(); };
  const pickThisMonth = () => { const b = monthBounds(_thisMonth); setInvMonth(_thisMonth); setDateFrom(b.from); setDateTo(b.to); };
  const inRange = (d) => {
    if (!d) return false;
    const day = d.slice(0, 10);
    if (dateFrom && day < dateFrom) return false;
    if (dateTo && day > dateTo) return false;
    return true;
  };
  // Paid invoices filter by their payment date — ACH sets paid_at, checks set printed_at;
  // unpaid invoices are always kept visible regardless of the selected period.
  const paidDateOf = (inv) => inv.paid_at || inv.printed_at || inv.invoice_date;
  const applyDateFilter = (list) => list.filter((inv) => inv.status !== "paid" || inRange(paidDateOf(inv)));

  const toggleSel = (id) => setSelIds((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]));

  const downloadFiles = async (list) => {
    const withFile = list.filter((i) => i.file);
    const chosen = selIds.filter((id) => withFile.some((i) => i.id === id));
    const ids = chosen.length ? chosen : withFile.map((i) => i.id);
    if (!ids.length) { toast.error("No invoice files available to download"); return; }
    setDownloading(true);
    try {
      const r = await api.get("/invoices/download-zip", { params: { property_id: propertyId, ids: ids.join(",") }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url; a.download = "invoices.zip";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${ids.length} file${ids.length > 1 ? "s" : ""}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Download failed");
    } finally { setDownloading(false); }
  };

  // full property (for bank settings + next check number)
  const [property, setProperty] = useState(null);
  const canSettings = can(user, ["admin", "regional", "finance"]);

  // print dialog
  const [printOpen, setPrintOpen] = useState(false);
  const [printInv, setPrintInv] = useState(null);
  const [printNumber, setPrintNumber] = useState("");
  const [printAmount, setPrintAmount] = useState("");

  // check settings dialog
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [bankForm, setBankForm] = useState(EMPTY_BANK);
  const [setSaving, setSetSaving] = useState(false);

  useEffect(() => {
    api.get("/properties").then((r) => {
      setProps(r.data);
      if (!propertyId && r.data[0]) setPropertyId(r.data[0].id);
    });
  }, []);

  const loadVendors = useCallback(() => {
    if (!propertyId) return;
    api.get("/vendors", { params: { property_id: propertyId } }).then((r) => setVendors(r.data)).catch(() => setVendors([]));
  }, [propertyId]);

  const loadInvoices = useCallback(() => {
    if (!propertyId) return;
    setInvoices(null);
    api.get("/invoices", { params: { property_id: propertyId } }).then((r) => setInvoices(r.data)).catch(() => setInvoices([]));
  }, [propertyId]);

  const loadProperty = useCallback(() => {
    if (!propertyId) return;
    api.get(`/properties/${propertyId}`).then((r) => setProperty(r.data)).catch(() => setProperty(null));
  }, [propertyId]);

  const loadCcReceipts = useCallback(() => {
    if (!propertyId) return;
    api.get("/cc-receipts", { params: { property_id: propertyId } }).then((r) => setCcData(r.data)).catch(() => setCcData(null));
  }, [propertyId]);

  const loadCcPayments = useCallback(() => {
    if (!propertyId) return;
    api.get("/cc-payments", { params: { property_id: propertyId } }).then((r) => setCcPayments(r.data)).catch(() => setCcPayments([]));
  }, [propertyId]);

  const loadDeleted = useCallback(() => {
    if (!canViewDeleted) return;
    api.get("/invoices/deleted", { params: propertyId ? { property_id: propertyId } : {} }).then((r) => setDeletedList(r.data)).catch(() => setDeletedList([]));
  }, [propertyId, canViewDeleted]);

  useEffect(() => { loadVendors(); loadInvoices(); loadProperty(); loadCcReceipts(); loadCcPayments(); loadDeleted(); }, [loadVendors, loadInvoices, loadProperty, loadCcReceipts, loadCcPayments, loadDeleted]);

  const promptAddCard = async () => {
    const last4 = window.prompt("Last 4 digits of the card:");
    if (last4 === null) return null;
    if (last4.replace(/\D/g, "").length < 4) { toast.error("Enter 4 digits"); return null; }
    const label = window.prompt("Card nickname (optional):", "") || "";
    try {
      const r = await api.post(`/properties/${propertyId}/cards`, { label, last4 });
      setProperty((p) => ({ ...p, credit_cards: [...(p?.credit_cards || []), r.data] }));
      toast.success("Card added to property");
      return r.data.id;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); return null; }
  };

  // batch check selection
  const openBatch = async () => {
    try {
      const r = await api.get("/invoices/pending-checks");
      setPendingChecks(r.data.items || []);
      setBatchSel((r.data.items || []).map((i) => i.id));
      setBatchOpen(true);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const printSelectedChecks = async () => {
    if (batchSel.length === 0) { toast.error("Select at least one check"); return; }
    setBatching(true);
    try {
      const res = await api.post("/invoices/batch-print-checks", { invoice_ids: batchSel, bank_ids: batchBankIds }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `batch_checks_${batchSel.length}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Printed ${batchSel.length} check(s)`);
      setBatchOpen(false);
      loadInvoices();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBatching(false); }
  };

  // deleted invoice archive
  const addDeletedComment = async (d) => {
    const text = (delCommentText[d.id] || "").trim();
    if (!text) return;
    try {
      await api.post(`/invoices/deleted/${d.id}/comments`, { text });
      setDelCommentText((m) => ({ ...m, [d.id]: "" }));
      loadDeleted();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const restoreDeleted = async (d) => {
    if (!window.confirm(`Restore invoice ${d.invoice_number || ""} from ${d.vendor_name || "vendor"} back to the active list?`)) return;
    try {
      await api.post(`/invoices/deleted/${d.id}/restore`);
      toast.success("Invoice restored");
      loadDeleted(); loadInvoices();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const purgeDeleted = async (d) => {
    if (!window.confirm(`Permanently delete this invoice? This cannot be undone.`)) return;
    try {
      await api.delete(`/invoices/deleted/${d.id}`);
      toast.success("Permanently deleted");
      loadDeleted();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // ---------- Vendors ----------
  const setVF = (k, v) => setVForm((f) => ({ ...f, [k]: v }));
  const openVendorNew = () => { setVEditId(null); setVForm(EMPTY_VENDOR); setVOpen(true); };
  const openVendorEdit = (v) => {
    setVEditId(v.id);
    setVForm({ name: v.name, contact_name: v.contact_name || "", phone: v.phone || "", email: v.email || "", address: v.address || "", city: v.city || "", state: v.state || "", zip: v.zip || "", account_number: v.account_number || "", notes: v.notes || "", default_cc_category: v.default_cc_category || "", portal_url: v.portal_url || "", portal_username: v.portal_username || "", portal_notes: v.portal_notes || "", __hasPw: !!v.has_portal_password });
    setVOpen(true);
  };
  const saveVendor = async () => {
    setVSaving(true);
    try {
      const { __hasPw, ...rest } = vForm;
      const payload = { property_id: propertyId, ...rest };
      if (vEditId) await api.patch(`/vendors/${vEditId}`, payload);
      else await api.post("/vendors", payload);
      toast.success(vEditId ? "Vendor updated" : "Vendor added");
      setVOpen(false);
      loadVendors();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setVSaving(false);
    }
  };
  const deleteVendor = async (v) => {
    if (!window.confirm(`Delete vendor ${v.name}?`)) return;
    try {
      await api.delete(`/vendors/${v.id}`);
      toast.success("Vendor deleted");
      loadVendors();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // ---------- Bulk vendors (add + edit in one grid) ----------
  const VENDOR_BULK_COLS = [
    { key: "name", label: "Vendor Name", w: "w-40" },
    { key: "contact_name", label: "Contact", w: "w-32" },
    { key: "phone", label: "Phone", w: "w-32" },
    { key: "email", label: "Email", w: "w-48" },
    { key: "account_number", label: "Account #", w: "w-28" },
    { key: "address", label: "Street Address", w: "w-48" },
    { key: "city", label: "City", w: "w-32" },
    { key: "state", label: "State", w: "w-16" },
    { key: "zip", label: "Zip", w: "w-24" },
    { key: "notes", label: "Notes", w: "w-40" },
  ];
  const blankVRow = () => ({ id: null, name: "", contact_name: "", phone: "", email: "", account_number: "", address: "", city: "", state: "", zip: "", notes: "" });
  const openBulkVendors = () => {
    const existing = vendors.map((v) => ({
      id: v.id, name: v.name || "", contact_name: v.contact_name || "", phone: v.phone || "",
      email: v.email || "", account_number: v.account_number || "", address: v.address || "",
      city: v.city || "", state: v.state || "", zip: v.zip || "", notes: v.notes || "",
    }));
    setBulkVRows([...existing, ...Array.from({ length: 5 }, blankVRow)]);
    setBulkVOpen(true);
  };
  const addBulkVRows = () => setBulkVRows((rows) => [...rows, ...Array.from({ length: 5 }, blankVRow)]);
  const saveBulkVendors = async () => {
    const rows = bulkVRows.filter((r) => (r.name || "").trim() || r.id);
    if (rows.length === 0) { toast.error("Enter at least one vendor name"); return; }
    setBulkVSaving(true);
    try {
      const { data } = await api.post("/vendors/bulk", { property_id: propertyId, vendors: rows });
      const parts = [];
      if (data.inserted) parts.push(`${data.inserted} added`);
      if (data.updated) parts.push(`${data.updated} updated`);
      if (data.errors?.length) {
        toast.warning(`${parts.join(", ") || "Done"}. Skipped: ${data.errors.map((e) => `row ${e.row} (${e.error})`).join("; ")}`);
      } else {
        toast.success(parts.join(", ") || "Saved");
      }
      setBulkVOpen(false);
      loadVendors();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBulkVSaving(false);
    }
  };

  // ---------- Invoices ----------
  const setIF = (k, v) => setIForm((f) => ({ ...f, [k]: v }));
  const openInvoiceNew = () => {
    if (vendors.length === 0) return toast.error("Add a vendor first");
    setIForm({ ...EMPTY_INVOICE, invoice_date: new Date().toISOString().slice(0, 10) });
    setIFile(null);
    setIOpen(true);
  };
  const saveInvoice = async () => {
    if (!iForm.vendor_id) return toast.error("Select a vendor");
    if (!iForm.invoice_date) return toast.error("Enter an invoice date");
    if (iForm.amount === "" || isNaN(Number(iForm.amount))) return toast.error("Enter a valid amount");
    if (iForm.autopay && !iForm.autopay_date) return toast.error("Enter the autopay date");
    setISaving(true);
    try {
      const fd = new FormData();
      if (iFile) fd.append("file", iFile);
      await api.post("/invoices", fd, {
        params: {
          property_id: propertyId,
          vendor_id: iForm.vendor_id,
          invoice_date: iForm.invoice_date,
          amount: Number(iForm.amount),
          invoice_number: iForm.invoice_number || "",
          memo: iForm.memo || "",
          autopay: !!iForm.autopay,
          autopay_date: iForm.autopay ? iForm.autopay_date : "",
        },
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Invoice uploaded");
      setIOpen(false);
      loadInvoices();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setISaving(false);
    }
  };
  const canEditAutopay = can(user, ["admin", "regional", "finance"]);
  const saveAutopay = async (inv, date) => {
    try {
      await api.post(`/invoices/${inv.id}/autopay`, { autopay: true, autopay_date: date });
      toast.success(`Autopay scheduled for ${fmtDate(date)}`);
      setAutopayInv(null); loadInvoices();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const turnOffAutopay = async (inv) => {
    if (!window.confirm("Turn Autopay off? The invoice goes back to Pending for approval.")) return;
    try { await api.post(`/invoices/${inv.id}/autopay`, { autopay: false }); toast.success("Autopay turned off"); loadInvoices(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const exportPaid = async () => {
    setExportingPaid(true);
    try {
      const r = await api.get("/invoices/paid-export", { params: { property_id: propertyId, start: dateFrom || "2000-01-01", end: dateTo || "2999-12-31" }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Paid_Invoices_${dateFrom}_to_${dateTo}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); } finally { setExportingPaid(false); }
  };
  const deleteInvoice = async (inv) => {
    const reason = window.prompt(`Delete invoice ${inv.invoice_number || ""} from ${inv.vendor_name || "vendor"}?\n\nType a reason (required) — it's saved with the archived invoice:`);
    if (reason === null) return;
    if (!reason.trim()) { toast.error("A reason is required to delete"); return; }
    try {
      await api.delete(`/invoices/${inv.id}`, { params: { reason: reason.trim() } });
      toast.success("Invoice moved to Deleted");
      loadInvoices();
      loadDeleted();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const toggleApprove = async (inv, approved) => {
    try {
      await api.post(`/invoices/${inv.id}/approve`, { approved });
      toast.success(approved ? "Invoice approved" : "Approval removed");
      loadInvoices();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  // approve method dialog (Check vs ACH)
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveInv, setApproveInv] = useState(null);
  const [approveMethod, setApproveMethod] = useState("check");
  const [approveDate, setApproveDate] = useState("");
  const [approveSplit, setApproveSplit] = useState(false);
  const [approveAmount, setApproveAmount] = useState("");
  const isAdmin = can(user, ["admin"]);
  const invBalance = (inv) => Math.round(((inv?.amount || 0) - (inv?.amount_paid || 0)) * 100) / 100;
  const openApprove = (inv) => {
    setApproveInv(inv); setApproveMethod("check"); setApproveDate(new Date().toISOString().slice(0, 10));
    setApproveSplit(false); setApproveAmount(String(invBalance(inv))); setApproveOpen(true);
  };
  const isGM = can(user, ["gm"]);
  const canResubmit = can(user, ["admin", "regional", "finance", "gm"]);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectInv, setRejectInv] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const openReject = (inv) => { setRejectInv(inv); setRejectReason(""); setRejectOpen(true); };
  const doReject = async () => {
    if (!rejectReason.trim()) return toast.error("Please add a reason so the GM knows what to fix");
    try {
      await api.post(`/invoices/${rejectInv.id}/reject`, { reason: rejectReason });
      toast.success("Invoice disapproved");
      setRejectOpen(false);
      loadInvoices();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const doResubmit = async (inv) => {
    if (!window.confirm("Resubmit this invoice for approval? It moves back to Pending.")) return;
    try {
      await api.post(`/invoices/${inv.id}/resubmit`, {});
      toast.success("Invoice resubmitted for approval");
      loadInvoices();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const doApprove = async () => {
    const balance = invBalance(approveInv);
    let payAmount = balance;
    if (approveSplit) {
      payAmount = parseFloat(approveAmount);
      if (!payAmount || payAmount <= 0) return toast.error("Enter a valid amount to pay now");
      if (payAmount > balance) return toast.error(`Amount can't exceed the ${money(balance)} balance`);
    }
    try {
      await api.post(`/invoices/${approveInv.id}/approve`, { approved: true, method: approveMethod, approved_date: approveDate || null, pay_amount: payAmount });
      const partial = payAmount < balance;
      toast.success(approveMethod === "ach"
        ? (partial ? `Paid ${money(payAmount)} via ACH · ${money(balance - payAmount)} balance remains` : "Approved & marked paid via ACH")
        : (partial ? `Approved ${money(payAmount)} for check · ${money(balance - payAmount)} balance remains` : "Approved for check"));
      setApproveOpen(false);
      loadInvoices();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const editApprovalDate = async (inv) => {
    const cur = inv.approved_at ? inv.approved_at.slice(0, 10) : new Date().toISOString().slice(0, 10);
    const val = window.prompt("Approval date (YYYY-MM-DD):", cur);
    if (val == null) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(val)) return toast.error("Enter a date as YYYY-MM-DD");
    try {
      await api.patch(`/invoices/${inv.id}/approval-date`, { approved_date: val });
      toast.success("Approval date updated");
      loadInvoices();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const editCheckNumber = async (inv) => {
    const val = window.prompt("Set check number for this invoice:", inv.check_number || (property?.bank?.current_check_number ?? 1001));
    if (val == null) return;
    const num = parseInt(val, 10);
    if (!num || num < 1) return toast.error("Enter a valid check number");
    try {
      await api.patch(`/invoices/${inv.id}/check-number`, { check_number: num });
      toast.success(`Check number set to #${num}`);
      loadInvoices();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const downloadBlob = (data, filename) => {
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };
  const reprintCheck = async (inv) => {
    try {
      const res = await api.post(`/invoices/${inv.id}/reprint-check`, {}, { responseType: "blob" });
      downloadBlob(res.data, `check_${inv.check_number}.pdf`);
      toast.success(`Check #${inv.check_number} re-generated`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const voidCheck = async (inv) => {
    if (!window.confirm(`Void check #${inv.check_number}? The invoice returns to Approved so you can renumber/reprint.`)) return;
    try {
      await api.post(`/invoices/${inv.id}/void-check`);
      toast.success("Check voided");
      loadInvoices();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  // Blank check printing (for QuickBooks / manual payroll checks)
  const [blankOpen, setBlankOpen] = useState(false);
  const [blankCount, setBlankCount] = useState("1");
  const [blankStart, setBlankStart] = useState("");
  const [blankBusy, setBlankBusy] = useState(false);
  const openBlank = () => {
    setBlankCount("1");
    const dflt = bankAccts.accounts.find((a) => a.id === bankAccts.default_bank_id);
    setBlankBankId(dflt?.id || null);
    setBlankStart(String(dflt?.current_check_number ?? property?.bank?.current_check_number ?? 1001));
    setBlankOpen(true);
  };
  const submitBlank = async () => {
    const count = parseInt(blankCount, 10);
    const start = parseInt(blankStart, 10);
    if (!count || count < 1 || count > 100) return toast.error("Enter a number of checks between 1 and 100");
    if (!start || start < 1) return toast.error("Enter a valid starting check number");
    setBlankBusy(true);
    try {
      const res = await api.post("/checks/print-blank", { property_id: propertyId, count, start_number: start, bank_id: blankBankId }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      toast.success(count > 1 ? `Generated ${count} blank checks starting #${start}` : `Generated blank check #${start}`);
      setBlankOpen(false);
      loadProperty();
    } catch (e) {
      let detail = e.response?.data?.detail;
      if (e.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text()).detail; } catch { /* noop */ }
      }
      toast.error(formatApiErrorDetail(detail));
    } finally {
      setBlankBusy(false);
    }
  };
  const viewFile = async (inv) => {
    try {
      const res = await api.get(`/invoices/${inv.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const type = res.data.type || inv.file?.content_type || "";
      setViewer({ url, type, name: inv.file?.filename || "Invoice" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // ---------- CC Expenses handlers ----------
  const setRF = (k, v) => setRForm((f) => ({ ...f, [k]: v }));
  const vendorById = (id) => vendors.find((v) => v.id === id);
  const openInvoicesForVendor = (vendorId) => (invoices || []).filter(
    (i) => i.vendor_id === vendorId && i.status !== "paid" && (Math.round(((i.amount || 0) - (i.amount_paid || 0)) * 100) / 100) > 0.005);
  const openReceiptNew = () => {
    setREditId(null);
    setRForm({ ...EMPTY_RECEIPT, date: new Date().toISOString().slice(0, 10) });
    setRFile(null);
    setROpen(true);
  };
  const openReceiptEdit = (r) => {
    setREditId(r.id);
    setRForm({ vendor_id: r.vendor_id || "", date: (r.date || "").slice(0, 10), amount: String(r.amount ?? ""), category: r.category || "", memo: r.memo || "", invoice_id: r.invoice_id || "", card_id: r.card_id || "", __hasFile: !!r.file });
    setRFile(null);
    setROpen(true);
  };
  const onReceiptVendor = (vid) => {
    const v = vendorById(vid);
    setRForm((f) => ({
      ...f,
      vendor_id: vid,
      invoice_id: "",
      category: f.category || (v?.default_cc_category || ""),
    }));
  };
  const saveReceipt = async () => {
    if (!rForm.vendor_id) return toast.error("Select a vendor");
    if (!rForm.date) return toast.error("Enter the purchase date");
    if (rForm.amount === "" || isNaN(Number(rForm.amount)) || Number(rForm.amount) <= 0) return toast.error("Enter a valid amount");
    if (!rEditId && !rFile) return toast.error("Please attach the receipt image or PDF");
    if (!rEditId && !rForm.card_id) return toast.error("Select which credit card was used");
    setRSaving(true);
    try {
      if (rEditId) {
        await api.patch(`/cc-receipts/${rEditId}`, {
          vendor_id: rForm.invoice_id ? undefined : rForm.vendor_id,
          date: rForm.invoice_id ? undefined : rForm.date,
          amount: rForm.invoice_id ? undefined : Number(rForm.amount),
          category: rForm.category, memo: rForm.memo, card_id: rForm.card_id || undefined,
        });
        if (rFile) {
          const ff = new FormData();
          ff.append("file", rFile);
          await api.post(`/cc-receipts/${rEditId}/file`, ff, { headers: { "Content-Type": "multipart/form-data" } });
        }
        toast.success("Receipt updated");
      } else {
        const fd = new FormData();
        fd.append("file", rFile);
        await api.post("/cc-receipts", fd, {
          params: {
            property_id: propertyId, vendor_id: rForm.vendor_id, date: rForm.date,
            amount: Number(rForm.amount), category: rForm.category || "", memo: rForm.memo || "",
            invoice_id: rForm.invoice_id || "", card_id: rForm.card_id || "",
          },
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success(rForm.invoice_id ? "Receipt added & applied to invoice" : "Receipt added");
      }
      setROpen(false);
      loadCcReceipts();
      loadVendors();
      if (rForm.invoice_id) loadInvoices();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRSaving(false);
    }
  };
  const deleteReceipt = async (r) => {
    if (!window.confirm(`Delete receipt from ${r.vendor_name || r.merchant}?`)) return;
    try {
      await api.delete(`/cc-receipts/${r.id}`);
      toast.success("Receipt deleted");
      loadCcReceipts();
      if (r.invoice_id) loadInvoices();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const viewReceiptFile = async (r) => {
    try {
      const res = await api.get(`/cc-receipts/${r.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const type = res.data.type || r.file?.content_type || "";
      setViewer({ url, type, name: r.file?.filename || "Receipt" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const openPay = () => {
    setPayDate(new Date().toISOString().slice(0, 10));
    setPayAmount("");
    setPaySel([]);
    setPayCard("");
    setPayOpen(true);
  };
  const togglePaySel = (id) => setPaySel((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]));
  const unpaidReceipts = (ccData?.receipts || []).filter((r) => r.status !== "settled");
  // group unpaid receipts by card for the "pay off a card" picker
  const unpaidByCard = unpaidReceipts.reduce((m, r) => {
    const k = r.card_id || "__none";
    if (!m[k]) m[k] = { card_id: r.card_id || "", label: r.card_label || "", last4: r.card_last4 || "", total: 0, count: 0 };
    m[k].total = Math.round((m[k].total + Number(r.amount || 0)) * 100) / 100;
    m[k].count += 1;
    return m;
  }, {});
  const payVisibleReceipts = payCard ? unpaidReceipts.filter((r) => (r.card_id || "") === payCard) : unpaidReceipts;
  const selectCardToPay = (cardId) => {
    setPayCard(cardId);
    if (cardId) {
      const rows = unpaidReceipts.filter((r) => (r.card_id || "") === cardId);
      setPaySel(rows.map((r) => r.id));
      setPayAmount(String(Math.round(rows.reduce((s, r) => s + Number(r.amount || 0), 0) * 100) / 100));
    } else {
      setPaySel([]);
    }
  };
  const paySelTotal = Math.round(unpaidReceipts.filter((r) => paySel.includes(r.id)).reduce((s, r) => s + Number(r.amount || 0), 0) * 100) / 100;
  const payTarget = Math.round((Number(payAmount) || 0) * 100) / 100;
  const payDiff = Math.round((payTarget - paySelTotal) * 100) / 100;
  const payMatches = payTarget > 0 && Math.abs(payDiff) < 0.005 && paySel.length > 0;
  const submitPay = async () => {
    if (!payMatches) return;
    setPaySaving(true);
    try {
      await api.post("/cc-payments", {
        property_id: propertyId, payment_date: payDate, amount: payTarget,
        receipt_ids: paySel, memo: "", card_id: payCard || "",
      });
      toast.success(`Card payment of ${money(payTarget)} reconciled against ${paySel.length} receipt${paySel.length > 1 ? "s" : ""}`);
      setPayOpen(false);
      loadCcReceipts();
      loadCcPayments();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setPaySaving(false);
    }
  };
  const undoPayment = async (p) => {
    if (!window.confirm(`Undo this ${money(p.amount)} card payment? Its ${p.receipt_count} receipt(s) return to Unpaid.`)) return;
    try {
      await api.delete(`/cc-payments/${p.id}`);
      toast.success("Card payment undone");
      loadCcReceipts();
      loadCcPayments();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const openCcInv = (inv) => {
    const bal = Math.round(((inv.amount || 0) - (inv.amount_paid || 0)) * 100) / 100;
    const v = vendorById(inv.vendor_id);
    setCcInv(inv);
    setCcInvForm({ amount: String(bal), category: v?.default_cc_category || "", date: new Date().toISOString().slice(0, 10), memo: "", card_id: "" });
    setCcInvFile(null);
    setCcInvOpen(true);
  };
  const setCIF = (k, v) => setCcInvForm((f) => ({ ...f, [k]: v }));
  const submitCcInv = async () => {
    if (!ccInv) return;
    const amt = Number(ccInvForm.amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    if (!ccInvFile) return toast.error("Please attach the receipt image or PDF");
    if (!ccInvForm.card_id) return toast.error("Select which credit card was used");
    setCcInvSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", ccInvFile);
      await api.post("/cc-receipts", fd, {
        params: {
          property_id: propertyId, vendor_id: ccInv.vendor_id, date: ccInvForm.date,
          amount: amt, category: ccInvForm.category || "", memo: ccInvForm.memo || "",
          invoice_id: ccInv.id, card_id: ccInvForm.card_id || "",
        },
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Credit card payment of ${money(amt)} recorded for ${ccInv.vendor_name}`);
      setCcInvOpen(false);
      loadInvoices();
      loadCcReceipts();
      loadVendors();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCcInvSaving(false);
    }
  };

  const openPrint = (inv) => {
    setPrintInv(inv);
    const next = property?.bank?.current_check_number ?? 1001;
    setPrintNumber(String(next));
    // Check prints for the amount approved at approval time (split decided there).
    const approved = inv.approved_amount != null
      ? inv.approved_amount
      : Math.round(((inv.amount || 0) - (inv.amount_paid || 0)) * 100) / 100;
    setPrintAmount(String(approved));
    const dflt = bankAccts.accounts.find((a) => a.id === bankAccts.default_bank_id);
    setPrintBankId(dflt?.id || null);
    if (dflt && inv.check_number == null) setPrintNumber(String(dflt.current_check_number ?? 1001));
    setPrintOpen(true);
  };
  const doPrintCheck = async () => {
    const inv = printInv;
    const num = parseInt(printNumber, 10);
    if (!num || num < 1) return toast.error("Enter a valid check number");
    const amt = parseFloat(printAmount);
    if (!amt || amt <= 0) return toast.error("Enter a valid check amount");
    const body = { check_number: num, amount: amt, bank_id: printBankId };
    setPrintingId(inv.id);
    try {
      const res = await api.post(`/invoices/${inv.id}/print-check`, body, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `check_${num}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Check #${num} generated`);
      setPrintOpen(false);
      loadInvoices();
      loadProperty();
      bankAccts.reload();
    } catch (e) {
      let detail = e.response?.data?.detail;
      if (e.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text()).detail; } catch { /* noop */ }
      }
      toast.error(formatApiErrorDetail(detail));
    } finally {
      setPrintingId(null);
    }
  };

  const loadBankForm = (b) => {
    setBankForm({
      company_name: b.company_name || "",
      bank_name: b.bank_name || "",
      routing_number: b.routing_number || "",
      account_number: b.account_number || "",
      current_check_number: b.current_check_number ?? 1001,
      check_stock_type: b.check_stock_type || "blank",
      print_offset_x: b.print_offset_x ?? 0,
      print_offset_y: b.print_offset_y ?? 0,
    });
  };
  const openSettings = () => {
    const dflt = bankAccts.accounts.find((a) => a.id === bankAccts.default_bank_id) || property?.bank || {};
    setSettingsBankId(dflt.id || null);
    loadBankForm(dflt);
    setSettingsOpen(true);
  };
  const setBF = (k, v) => setBankForm((f) => ({ ...f, [k]: v }));
  const saveSettings = async () => {
    setSetSaving(true);
    try {
      const accounts = bankAccts.accounts.map((a) => (a.id === settingsBankId ? { ...a, ...bankForm,
        current_check_number: Number(bankForm.current_check_number) || 1001,
        print_offset_x: Number(bankForm.print_offset_x) || 0,
        print_offset_y: Number(bankForm.print_offset_y) || 0 } : a));
      await saveBankAccounts(propertyId, accounts, bankAccts.default_bank_id);
      toast.success("Check settings saved");
      setSettingsOpen(false);
      loadProperty();
      bankAccts.reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSetSaving(false);
    }
  };

  const paidInvoices = (invoices || []).filter((i) => i.status === "paid")
    .sort((a, b) => (a.vendor_name || "").localeCompare(b.vendor_name || "", undefined, { sensitivity: "base" }) || (paidDateOf(b) || "").localeCompare(paidDateOf(a) || ""));
  const openInvoices = (invoices || []).filter((i) => i.status !== "paid");

  const renderInvoiceTable = (rawList, emptyMsg) => {
    const list = sortInvoices(rawList, invSort);
    const sortHead = (key, label) => (
      <TableHead>
        <button type="button" className="inline-flex items-center gap-1 hover:text-foreground" onClick={() => setInvSort((s) => (s.key === key ? (s.dir === "asc" ? { key, dir: "desc" } : { key: "", dir: "" }) : { key, dir: "asc" }))} data-testid={`inv-sort-${key}`}>
          {label}
          {invSort.key === key ? (invSort.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
        </button>
      </TableHead>
    );
    const filesInList = list.filter((i) => i.file);
    const selInList = selIds.filter((id) => filesInList.some((i) => i.id === id));
    const selAll = list.filter((i) => selIds.includes(i.id));
    const allSel = list.length > 0 && selAll.length === list.length;
    const dlLabel = selInList.length ? `Download Selected (${selInList.length})` : `Download All Files${filesInList.length ? ` (${filesInList.length})` : ""}`;
    return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1">
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => shiftMonth(-1)} data-testid="inv-month-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <span className="min-w-[130px] text-center text-sm font-semibold" data-testid="inv-month-label">{invMonth ? fmtMonth(invMonth) : "Custom range"}</span>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => shiftMonth(1)} data-testid="inv-month-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setInvMonth(""); }} className="h-8 w-[150px]" data-testid="inv-date-from" />
            <span>to</span>
            <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setInvMonth(""); }} className="h-8 w-[150px]" data-testid="inv-date-to" />
            <Button size="sm" variant="ghost" className="h-8" onClick={pickThisMonth} data-testid="inv-this-month">This month</Button>
            <Button size="sm" variant="ghost" className="h-8" onClick={pickLast3} data-testid="inv-last-3-months">Last 3 months</Button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canResubmit && propertyId && (
            <Button size="sm" onClick={() => setSendApprovalFor(selAll)} disabled={!selAll.length} data-testid="send-for-approval-btn"
              title={selAll.length ? "" : "Tick invoices first"}>
              <Send className="mr-2 h-4 w-4" /> Send for Approval{selAll.length ? ` (${selAll.length})` : ""}
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => downloadFiles(list)} disabled={downloading || filesInList.length === 0} data-testid="download-invoice-files-btn">
            {downloading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} {dlLabel}
          </Button>
        </div>
      </div>
      <Card className="rounded-none border">
      {invoices === null ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : list.length === 0 ? (
        <div className="p-12 text-center">
          <Receipt className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">{emptyMsg}</p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8">
                <Checkbox checked={allSel} disabled={list.length === 0}
                  onCheckedChange={(v) => setSelIds(v ? list.map((i) => i.id) : [])}
                  aria-label="Select all" data-testid="select-all-invoices" />
              </TableHead>
              {sortHead("date", "Date")}
              {sortHead("vendor", "Vendor")}
              <TableHead>Invoice #</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              {sortHead("status", "Status")}
              <TableHead>Approved By</TableHead>
              <TableHead>File</TableHead>
              <TableHead>Approve</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((inv) => (
              <TableRow key={inv.id} className={inv.status === "paid" && !inv.file ? "bg-red-50 hover:bg-red-100 dark:bg-red-950/30 [&>td:first-child]:border-l-4 [&>td:first-child]:border-l-red-500" : ""}
                data-testid={`invoice-row-${inv.id}`} data-missing-invoice={inv.status === "paid" && !inv.file ? "true" : undefined}>
                <TableCell>
                  <Checkbox checked={selIds.includes(inv.id)} onCheckedChange={() => toggleSel(inv.id)}
                    aria-label="Select invoice" data-testid={`select-invoice-${inv.id}`} />
                </TableCell>
                <TableCell className="text-muted-foreground">{inv.invoice_date ? fmtDate(inv.invoice_date) : "—"}</TableCell>
                <TableCell className="font-medium">{inv.vendor_name}</TableCell>
                <TableCell className="font-mono text-xs">{inv.invoice_number || "—"}</TableCell>
                <TableCell className="text-right font-mono">{money(inv.amount)}</TableCell>
                <TableCell>
                  <Badge className={`rounded-none ${STATUS_STYLE[inv.status] || ""}`}>{STATUS_LABEL[inv.status] || inv.status}</Badge>
                  {inv.paid_via === "Credit Card" && <span className="ml-1 text-xs font-semibold text-primary">CC</span>}
                  {inv.approval_method === "ach" && <span className="ml-1 text-xs font-semibold text-primary">ACH</span>}
                  {inv.autopay_date && (inv.status === "scheduled" || inv.paid_via === "Autopay") && (
                    <div className="mt-0.5 text-[0.7rem] font-semibold text-sky-700 dark:text-sky-400" data-testid={`invoice-autopay-${inv.id}`}>Autopay · {fmtDate(inv.autopay_date)}{inv.autopay_set_by ? ` · set by ${inv.autopay_set_by}` : ""}</div>
                  )}
                  {inv.check_number && <span className="ml-2 text-xs text-muted-foreground">#{inv.check_number}</span>}
                  {inv.status === "paid" && inv.paid_at && <div className="text-[0.7rem] text-muted-foreground">Paid {fmtDate(inv.paid_at.slice(0, 10))}</div>}
                  <OwnerApprovalBadge inv={inv} />
                  {inv.printed_by && (
                    <div className="text-[0.7rem] text-muted-foreground" data-testid={`invoice-printed-${inv.id}`}>
                      Printed by {inv.printed_by}{inv.printed_at ? ` · ${fmtDate(inv.printed_at.slice(0, 10))}` : ""}
                    </div>
                  )}
                  {(inv.amount_paid || 0) > 0 && inv.status !== "paid" && (
                    <div className="text-[0.7rem] font-semibold text-amber-700 dark:text-amber-400" data-testid={`invoice-partial-${inv.id}`}>
                      Paid {money(inv.amount_paid)} of {money(inv.amount)} · Balance {money(Math.round((inv.amount - inv.amount_paid) * 100) / 100)}
                    </div>
                  )}
                  {inv.status === "approved" && inv.approved_amount != null && inv.approved_amount < Math.round((inv.amount - (inv.amount_paid || 0)) * 100) / 100 && (
                    <div className="text-[0.7rem] font-semibold text-blue-700 dark:text-blue-400" data-testid={`invoice-approved-amt-${inv.id}`}>
                      Approved to pay {money(inv.approved_amount)} (split)
                    </div>
                  )}
                  {inv.status === "rejected" && inv.rejection_reason && (
                    <div className="mt-1 max-w-[220px] text-xs text-destructive" data-testid={`invoice-reject-reason-${inv.id}`}>
                      <span className="font-semibold">Reason:</span> {inv.rejection_reason}
                      <div className="text-[0.7rem] text-muted-foreground">by {inv.rejected_by}{inv.rejected_at ? ` · ${fmtDate(inv.rejected_at.slice(0, 10))}` : ""}</div>
                    </div>
                  )}
                </TableCell>
                <TableCell data-testid={`invoice-approvedby-${inv.id}`}>
                  {inv.approved_by ? (
                    <div className="text-xs">
                      <div className="font-medium">{inv.approved_by}</div>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <span>{inv.approved_at ? fmtDate(inv.approved_at.slice(0, 10)) : "—"}</span>
                        {isAdmin && (
                          <button onClick={() => editApprovalDate(inv)} className="text-primary hover:underline" data-testid={`edit-approval-date-${inv.id}`}>edit</button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell>
                  {inv.file ? (
                    <InvoiceHoverPreview inv={inv}>
                      <Button size="sm" variant="ghost" onClick={() => viewFile(inv)} data-testid={`view-invoice-file-${inv.id}`}>
                        <Eye className="mr-1 h-4 w-4" /> View
                      </Button>
                    </InvoiceHoverPreview>
                  ) : canResubmit ? (
                    <div>
                      <InvoiceFileUpload inv={inv} onDone={loadInvoices} />
                      {inv.status === "paid" && <div className="mt-0.5 text-[0.65rem] font-semibold text-red-600" data-testid={`invoice-missing-file-${inv.id}`}>Invoice missing</div>}
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell>
                  {inv.status === "pending" ? (
                    <div className="flex flex-wrap items-center gap-1.5">
                      {inv.can_approve && <Button size="sm" variant="outline" onClick={() => openApprove(inv)} data-testid={`approve-invoice-${inv.id}`}>Approve</Button>}
                      {canManage && <button onClick={() => setAutopayInv(inv)} className="text-xs font-semibold text-sky-700 hover:underline dark:text-sky-400" data-testid={`autopay-invoice-${inv.id}`}>Autopay</button>}
                      {inv.can_approve && <button onClick={() => openReject(inv)} className="text-xs text-destructive hover:underline" data-testid={`disapprove-invoice-${inv.id}`}>Disapprove</button>}
                      {!inv.can_approve && !canManage && <span className="text-xs text-muted-foreground">—</span>}
                    </div>
                  ) : inv.status === "scheduled" ? (
                    <div className="flex flex-wrap items-center gap-1.5 text-xs">
                      <span className="text-sky-700 dark:text-sky-400">Autopay {fmtDate(inv.autopay_date)}</span>
                      {canEditAutopay && (
                        <>
                          <button onClick={() => setAutopayInv(inv)} className="text-primary hover:underline" data-testid={`autopay-edit-${inv.id}`}>edit</button>
                          <button onClick={() => turnOffAutopay(inv)} className="text-destructive hover:underline" data-testid={`autopay-off-${inv.id}`}>turn off</button>
                        </>
                      )}
                    </div>
                  ) : inv.status === "rejected" ? (
                    canResubmit ? (
                      <Button size="sm" variant="outline" onClick={() => doResubmit(inv)} data-testid={`resubmit-invoice-${inv.id}`}>Resubmit</Button>
                    ) : <span className="text-xs text-muted-foreground">Unapproved</span>
                  ) : (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <span>{inv.paid_via === "Credit Card" ? "Credit Card" : inv.paid_via === "Autopay" ? "Autopay" : inv.approval_method === "ach" ? "ACH" : "Check"}</span>
                      {inv.can_approve && inv.status === "approved" && (
                        <button onClick={() => toggleApprove(inv, false)} className="text-destructive hover:underline" data-testid={`unapprove-invoice-${inv.id}`}>undo</button>
                      )}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex flex-wrap justify-end gap-1">
                    {inv.can_print && inv.status === "approved" && inv.approval_method !== "ach" && (
                      <Button size="sm" variant="outline" onClick={() => openPrint(inv)} disabled={printingId === inv.id} data-testid={`print-check-${inv.id}`}>
                        {printingId === inv.id ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Printer className="mr-1 h-4 w-4" />} Print Check
                      </Button>
                    )}
                    {isAdmin && (inv.status === "approved" || inv.status === "paid") && inv.approval_method !== "ach" && (
                      <Button size="sm" variant="ghost" onClick={() => editCheckNumber(inv)} data-testid={`edit-checknum-${inv.id}`}>Check #</Button>
                    )}
                    {isAdmin && inv.status === "paid" && inv.check_number && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => reprintCheck(inv)} data-testid={`reprint-check-${inv.id}`}><Printer className="mr-1 h-4 w-4" /> Reprint</Button>
                        <Button size="sm" variant="ghost" onClick={() => voidCheck(inv)} data-testid={`void-check-${inv.id}`}>Void</Button>
                      </>
                    )}
                    {canManage && inv.status !== "paid" && inv.status !== "rejected" && (
                      <Button size="sm" variant="outline" onClick={() => openCcInv(inv)} data-testid={`pay-cc-invoice-${inv.id}`}>
                        <CreditCard className="mr-1 h-4 w-4" /> Pay by CC
                      </Button>
                    )}
                    {(canDeleteVendor && inv.status !== "paid") || isAdmin ? (
                      <Button size="icon" variant="ghost" onClick={() => deleteInvoice(inv)} data-testid={`delete-invoice-${inv.id}`}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    ) : null}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
    </>
    );
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-caps text-primary">Accounts Payable</div>
          <h1 className="font-heading text-3xl font-black tracking-tight lg:text-4xl">Invoices, Receipts &amp; Checks</h1>
          <p className="mt-1 text-sm text-muted-foreground">Upload vendor invoices, approve them, and print checks.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={propertyId} onValueChange={setPropertyId}>
            <SelectTrigger className="w-56" data-testid="inv-property-select"><SelectValue placeholder="Select property" /></SelectTrigger>
            <SelectContent>{props.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}</SelectContent>
          </Select>
          {canSettings && (
            <Button variant="outline" onClick={openSettings} disabled={!property} data-testid="check-settings-btn">
              <Settings className="mr-2 h-4 w-4" /> Check Settings
            </Button>
          )}
          {canPrintBlank && (
            <Button variant="outline" onClick={openBlank} disabled={!property} data-testid="print-blank-checks-btn">
              <Printer className="mr-2 h-4 w-4" /> Print Blank Checks
            </Button>
          )}
          {isAdmin && (
            <Button onClick={openBatch} disabled={batching} data-testid="batch-print-btn">
              {batching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Printer className="mr-2 h-4 w-4" />} Batch Print Checks
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="invoices" onValueChange={onTabChange}>
        <TabsList className="rounded-none">
          <TabsTrigger value="invoices" data-testid="tab-invoices"><Receipt className="mr-2 h-4 w-4" /> Invoices</TabsTrigger>
          <TabsTrigger value="paid" data-testid="tab-paid"><CheckCircle2 className="mr-2 h-4 w-4" /> Paid Invoices{paidInvoices.length > 0 ? ` (${paidInvoices.length})` : ""}</TabsTrigger>
          <TabsTrigger value="receipts" data-testid="tab-receipts"><CreditCard className="mr-2 h-4 w-4" /> CC Receipts{ccData?.summary?.unpaid_count ? ` (${ccData.summary.unpaid_count})` : ""}</TabsTrigger>
          <TabsTrigger value="cc-payments" data-testid="tab-cc-payments"><CheckCircle2 className="mr-2 h-4 w-4" /> Card Payments</TabsTrigger>
          <TabsTrigger value="vendors" data-testid="tab-vendors"><Building className="mr-2 h-4 w-4" /> Vendors</TabsTrigger>
          {canViewDeleted && <TabsTrigger value="deleted" data-testid="tab-deleted"><Trash2 className="mr-2 h-4 w-4" /> Deleted{deletedList.length > 0 ? ` (${deletedList.length})` : ""}</TabsTrigger>}
        </TabsList>

        {/* Invoices tab */}
        <TabsContent value="invoices" className="mt-4">
          <div className="mb-4 flex justify-end">
            {canManage && (
              <Button onClick={openInvoiceNew} disabled={!propertyId} data-testid="add-invoice-btn">
                <Plus className="mr-2 h-4 w-4" /> Upload Invoice
              </Button>
            )}
          </div>
          {renderInvoiceTable(applyDateFilter(openInvoices), "No invoices yet.")}
          <ApDocuments propertyId={propertyId} user={user} />
        </TabsContent>

        {/* Paid Invoices tab */}
        <TabsContent value="paid" className="mt-4" data-testid="paid-invoices-tab">
          <div className="mb-3 flex justify-end">
            <Button size="sm" variant="outline" onClick={exportPaid} disabled={!propertyId || exportingPaid} data-testid="export-paid-invoices-btn">
              {exportingPaid ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} Export Excel
            </Button>
          </div>
          {renderInvoiceTable(applyDateFilter(paidInvoices), "No paid invoices in this period.")}
        </TabsContent>

        {/* CC Receipts tab */}
        <TabsContent value="receipts" className="mt-4" data-testid="receipts-tab">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="rounded-none border border-border bg-muted/40 px-4 py-2">
                <div className="label-caps text-muted-foreground">Awaiting bank payment</div>
                <div className="font-heading text-xl font-black" data-testid="cc-unpaid-total">
                  {money(ccData?.summary?.unpaid_total || 0)}
                  <span className="ml-2 text-sm font-normal text-muted-foreground">{ccData?.summary?.unpaid_count || 0} receipt(s)</span>
                </div>
              </div>
              <Select value={ccFilter} onValueChange={setCcFilter}>
                <SelectTrigger className="w-48" data-testid="cc-filter-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unpaid">Paid by Card</SelectItem>
                  <SelectItem value="settled">Settled (from bank)</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              {ccData?.can_pay && (
                <Button variant="outline" onClick={openPay} disabled={!propertyId || unpaidReceipts.length === 0} data-testid="pay-card-btn">
                  <CreditCard className="mr-2 h-4 w-4" /> Pay Credit Card from Bank
                </Button>
              )}
              {ccData?.can_manage && (
                <Button onClick={openReceiptNew} disabled={!propertyId} data-testid="add-receipt-btn">
                  <Plus className="mr-2 h-4 w-4" /> Add Receipt
                </Button>
              )}
            </div>
          </div>
          <Card className="rounded-none border">
            {(() => {
              const list = (ccData?.receipts || []).filter((r) =>
                ccFilter === "all" ? true : ccFilter === "settled" ? r.status === "settled" : r.status !== "settled");
              if (list.length === 0) {
                return (
                  <div className="p-12 text-center">
                    <CreditCard className="mx-auto h-8 w-8 text-muted-foreground" />
                    <p className="mt-3 text-sm text-muted-foreground">No receipts to show.</p>
                  </div>
                );
              }
              return (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Vendor</TableHead>
                      <TableHead>Card</TableHead>
                      <TableHead>Invoice</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Memo</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {list.map((r) => (
                      <TableRow key={r.id} data-testid={`receipt-row-${r.id}`}>
                        <TableCell className="whitespace-nowrap">{fmtDate(r.date)}</TableCell>
                        <TableCell className="font-medium">{r.vendor_name || r.merchant}</TableCell>
                        <TableCell data-testid={`receipt-card-${r.id}`}>
                          {r.card_last4
                            ? <span className="whitespace-nowrap text-sm">{r.card_label || "Card"} <span className="font-mono text-muted-foreground">••{r.card_last4}</span></span>
                            : <span className="text-xs text-amber-600">Not set</span>}
                        </TableCell>
                        <TableCell>
                          {r.invoice_id ? (
                            <Badge variant="outline" className="rounded-none" data-testid={`receipt-invoice-${r.id}`}>{r.invoice_number ? `#${r.invoice_number}` : "Linked"}</Badge>
                          ) : <span className="text-xs text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="text-muted-foreground">{r.category_name || <span className="text-destructive">Uncategorized</span>}</TableCell>
                        <TableCell className="max-w-[16rem] truncate text-muted-foreground">{r.memo || "—"}</TableCell>
                        <TableCell className="text-right font-mono">{money(r.amount)}</TableCell>
                        <TableCell>
                          <Badge className={`rounded-none ${r.status === "settled" ? "bg-primary text-primary-foreground" : "bg-[hsl(var(--success))] text-white"}`}>
                            {r.status === "settled" ? "Settled" : "Paid by Credit Card"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {r.file && (
                            <Button size="icon" variant="ghost" onClick={() => viewReceiptFile(r)} data-testid={`view-receipt-${r.id}`}><Eye className="h-4 w-4" /></Button>
                          )}
                          {ccData?.can_manage && (
                            <Button size="icon" variant="ghost" onClick={() => openReceiptEdit(r)} data-testid={`edit-receipt-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                          )}
                          {ccData?.can_manage && r.status !== "settled" && (
                            <Button size="icon" variant="ghost" onClick={() => deleteReceipt(r)} data-testid={`delete-receipt-${r.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              );
            })()}
          </Card>
          <p className="mt-3 text-xs text-muted-foreground">
            Receipts are purchases already paid with the company card. They post to the P&amp;L by purchase date under their category. Use <strong>Pay Credit Card from Bank</strong> to reconcile them against the payment made from the bank.
          </p>
        </TabsContent>

        {/* Card Payments tab */}
        <TabsContent value="cc-payments" className="mt-4" data-testid="cc-payments-tab">
          <Card className="rounded-none border">
            {ccPayments.length === 0 ? (
              <div className="p-12 text-center">
                <CheckCircle2 className="mx-auto h-8 w-8 text-muted-foreground" />
                <p className="mt-3 text-sm text-muted-foreground">No card payments reconciled yet.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Payment Date</TableHead>
                    <TableHead>Card</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right"># Receipts</TableHead>
                    <TableHead>Reconciled By</TableHead>
                    {ccData?.can_pay && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ccPayments.map((p) => (
                    <TableRow key={p.id} data-testid={`cc-payment-row-${p.id}`}>
                      <TableCell className="whitespace-nowrap">{fmtDate(p.payment_date)}</TableCell>
                      <TableCell className="whitespace-nowrap text-sm">{p.card_last4 ? `${p.card_label || "Card"} ••${p.card_last4}` : <span className="text-muted-foreground">Multiple / —</span>}</TableCell>
                      <TableCell className="text-right font-mono">{money(p.amount)}</TableCell>
                      <TableCell className="text-right">{p.receipt_count}</TableCell>
                      <TableCell className="text-muted-foreground">{p.created_by || "—"}</TableCell>
                      {ccData?.can_pay && (
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" onClick={() => undoPayment(p)} data-testid={`undo-cc-payment-${p.id}`}>
                            <Undo2 className="mr-1 h-4 w-4" /> Undo
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        {/* Vendors tab */}
        <TabsContent value="vendors" className="mt-4">
          <div className="mb-4 flex justify-end gap-2">
            {canManage && (
              <Button variant="outline" onClick={openBulkVendors} disabled={!propertyId} data-testid="bulk-vendor-btn">
                <ClipboardPaste className="mr-2 h-4 w-4" /> Bulk Add / Edit
              </Button>
            )}
            {canManage && (
              <Button onClick={openVendorNew} disabled={!propertyId} data-testid="add-vendor-btn">
                <Plus className="mr-2 h-4 w-4" /> Add Vendor
              </Button>
            )}
          </div>
          <Card className="rounded-none border">
            {vendors.length === 0 ? (
              <div className="p-12 text-center">
                <Building className="mx-auto h-8 w-8 text-muted-foreground" />
                <p className="mt-3 text-sm text-muted-foreground">No vendors yet.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Account #</TableHead>
                    <TableHead>Online Portal</TableHead>
                    {canManage && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vendors.map((v) => (
                    <TableRow key={v.id} data-testid={`vendor-row-${v.id}`}>
                      <TableCell className="font-medium">{v.name}</TableCell>
                      <TableCell className="text-muted-foreground">{v.contact_name || "—"}</TableCell>
                      <TableCell className="text-muted-foreground">{v.phone || "—"}</TableCell>
                      <TableCell className="text-muted-foreground">{v.email || "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{v.account_number || "—"}</TableCell>
                      <TableCell><VendorPortalCell v={v} canSecret={canVendorSecret} /></TableCell>
                      {canManage && (
                        <TableCell className="text-right">
                          <Button size="icon" variant="ghost" onClick={() => openVendorEdit(v)} data-testid={`edit-vendor-${v.id}`}><Pencil className="h-4 w-4" /></Button>
                          {canDeleteVendor && (
                            <Button size="icon" variant="ghost" onClick={() => deleteVendor(v)} data-testid={`delete-vendor-${v.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        {canViewDeleted && (
          <TabsContent value="deleted" className="mt-4" data-testid="deleted-tab">
            <Card className="rounded-none border p-4">
              <p className="mb-3 text-sm text-muted-foreground">Deleted invoices are archived here with the reason and a comment thread. {propertyId ? "Showing the selected property." : ""}</p>
              {deletedList.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">No deleted invoices.</p>
              ) : (
                <div className="space-y-3">
                  {deletedList.map((d) => (
                    <div key={d.id} className="rounded-lg border border-border p-4" data-testid={`deleted-${d.id}`}>
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="font-heading text-base font-bold">{d.vendor_name || "—"} {d.invoice_number ? <span className="text-muted-foreground">#{d.invoice_number}</span> : null}</div>
                          <div className="text-sm text-muted-foreground">{money(d.amount)} · was <span className="uppercase">{d.prev_status}</span> · deleted by {d.deleted_by} on {fmtDT(d.deleted_at)}</div>
                        </div>
                        <div className="flex gap-2">
                          {isAdmin && <Button size="sm" variant="outline" onClick={() => restoreDeleted(d)} data-testid={`restore-${d.id}`}><Undo2 className="mr-1 h-4 w-4" /> Restore</Button>}
                          {isAdmin && <Button size="sm" variant="ghost" onClick={() => purgeDeleted(d)} className="text-red-500 hover:text-red-600" data-testid={`purge-${d.id}`}><Trash2 className="mr-1 h-4 w-4" /> Delete forever</Button>}
                        </div>
                      </div>
                      <div className="mt-3 space-y-2 border-t border-border pt-3">
                        {(d.comments || []).map((c) => (
                          <div key={c.id} className="text-sm">
                            <span className={`font-semibold ${c.is_reason ? "text-red-600 dark:text-red-400" : ""}`}>{c.by}</span>
                            <span className="ml-2 text-xs text-muted-foreground">{fmtDT(c.at)}{c.is_reason ? " · reason" : ""}</span>
                            <div className="text-foreground">{c.text}</div>
                          </div>
                        ))}
                        <div className="flex gap-2 pt-1">
                          <Input value={delCommentText[d.id] || ""} onChange={(e) => setDelCommentText((m) => ({ ...m, [d.id]: e.target.value }))}
                            onKeyDown={(e) => e.key === "Enter" && addDeletedComment(d)} placeholder="Add a comment…" data-testid={`del-comment-input-${d.id}`} />
                          <Button size="sm" variant="outline" onClick={() => addDeletedComment(d)} data-testid={`del-comment-send-${d.id}`}>Comment</Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>
        )}
      </Tabs>

      <SendInvoiceApproval open={!!sendApprovalFor} onClose={() => setSendApprovalFor(null)} propertyId={propertyId}
        invoices={sendApprovalFor || []} onSent={() => { setSelIds([]); loadInvoices(); }} />

      {/* Vendor dialog */}
      <Dialog open={vOpen} onOpenChange={setVOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="vendor-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{vEditId ? "Edit Vendor" : "Add Vendor"}</DialogTitle>
            <DialogDescription>Vendor details are used on printed checks.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2 space-y-1.5">
              <Label>Vendor Name *</Label>
              <Input value={vForm.name} onChange={(e) => setVF("name", e.target.value)} data-testid="vendor-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Contact Name</Label>
              <Input value={vForm.contact_name} onChange={(e) => setVF("contact_name", e.target.value)} data-testid="vendor-contact" />
            </div>
            <div className="space-y-1.5">
              <Label>Account #</Label>
              <Input value={vForm.account_number} onChange={(e) => setVF("account_number", e.target.value)} data-testid="vendor-account" />
            </div>
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input value={vForm.phone} onChange={(e) => setVF("phone", e.target.value)} data-testid="vendor-phone" />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" value={vForm.email} onChange={(e) => setVF("email", e.target.value)} data-testid="vendor-email" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Street Address</Label>
              <Input value={vForm.address} onChange={(e) => setVF("address", e.target.value)} placeholder="e.g. 2252 NW 164th St" data-testid="vendor-address" />
            </div>
            <div className="space-y-1.5">
              <Label>City</Label>
              <Input value={vForm.city} onChange={(e) => setVF("city", e.target.value)} data-testid="vendor-city" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label>State</Label>
                <Input value={vForm.state} onChange={(e) => setVF("state", e.target.value)} placeholder="OK" data-testid="vendor-state" />
              </div>
              <div className="space-y-1.5">
                <Label>Zip</Label>
                <Input value={vForm.zip} onChange={(e) => setVF("zip", e.target.value)} placeholder="73013" data-testid="vendor-zip" />
              </div>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Notes</Label>
              <Textarea value={vForm.notes} onChange={(e) => setVF("notes", e.target.value)} data-testid="vendor-notes" />
            </div>
            <VendorPortalFields key={`${vEditId}-${vOpen}`} form={vForm} setF={setVF} vendorId={vEditId} hasPassword={!!vForm.__hasPw} canSecret={canVendorSecret} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVOpen(false)}>Cancel</Button>
            <Button onClick={saveVendor} disabled={vSaving || !vForm.name} data-testid="save-vendor-btn">
              {vSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {vEditId ? "Update" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk vendor dialog */}
      <Dialog open={bulkVOpen} onOpenChange={setBulkVOpen}>
        <DialogContent className="max-w-5xl" data-testid="bulk-vendor-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Bulk Add / Edit Vendors</DialogTitle>
            <DialogDescription>
              Existing vendors are loaded for editing; blank rows at the bottom add new ones. Paste rows from a spreadsheet
              (Vendor Name, Contact, Phone, Email, Account #, Address, Notes) into any cell, then save all at once.
            </DialogDescription>
          </DialogHeader>
          <BulkGrid cols={VENDOR_BULK_COLS} rows={bulkVRows} setRows={setBulkVRows} />
          <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
            <Button variant="outline" size="sm" onClick={addBulkVRows} data-testid="bulk-vendor-add-rows-btn">
              <Plus className="mr-2 h-4 w-4" /> Add 5 Rows
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setBulkVOpen(false)}>Cancel</Button>
              <Button onClick={saveBulkVendors} disabled={bulkVSaving} data-testid="bulk-vendor-save-btn">
                {bulkVSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save All
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice dialog */}
      <Dialog open={iOpen} onOpenChange={setIOpen}>
        <DialogContent data-testid="invoice-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Upload Invoice</DialogTitle>
            <DialogDescription>Attach a PDF or image (max 10MB).</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2 space-y-1.5">
              <Label>Vendor *</Label>
              <Select value={iForm.vendor_id} onValueChange={(v) => setIF("vendor_id", v)}>
                <SelectTrigger data-testid="invoice-vendor"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                <SelectContent>{vendors.map((v) => (<SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Invoice Date *</Label>
              <Input type="date" value={iForm.invoice_date} onChange={(e) => setIF("invoice_date", e.target.value)} data-testid="invoice-date" />
            </div>
            <div className="space-y-1.5">
              <Label>Amount ($) *</Label>
              <Input type="text" inputMode="decimal" value={iForm.amount} onChange={(e) => setIF("amount", e.target.value)} data-testid="invoice-amount" />
            </div>
            <div className="space-y-1.5">
              <Label>Invoice #</Label>
              <Input value={iForm.invoice_number} onChange={(e) => setIF("invoice_number", e.target.value)} data-testid="invoice-number" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Memo</Label>
              <Input value={iForm.memo} onChange={(e) => setIF("memo", e.target.value)} data-testid="invoice-memo" />
            </div>
            <div className="col-span-2 rounded-sm border border-sky-300/70 bg-sky-50/60 p-3 dark:bg-sky-950/20">
              <label className="flex items-center gap-2 text-sm font-medium" data-testid="invoice-autopay-label">
                <Checkbox checked={!!iForm.autopay} onCheckedChange={(v) => setIF("autopay", !!v)} data-testid="invoice-autopay-checkbox" />
                Already set up for Autopay
              </label>
              {iForm.autopay && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Label className="text-xs">Autopay date *</Label>
                  <Input type="date" value={iForm.autopay_date} onChange={(e) => setIF("autopay_date", e.target.value)} className="h-8 w-44" data-testid="invoice-autopay-date" />
                  <span className="text-[0.7rem] text-muted-foreground">Skips approval and check printing — marked Paid on this date.</span>
                </div>
              )}
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Invoice File (PDF / JPG / PNG)</Label>
              <label className="flex cursor-pointer items-center gap-2 border border-dashed border-border px-3 py-3 text-sm text-muted-foreground hover:border-primary">
                <Upload className="h-4 w-4" />
                {iFile ? iFile.name : "Choose file"}
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => setIFile(e.target.files?.[0] || null)} data-testid="invoice-file-input" />
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIOpen(false)}>Cancel</Button>
            <Button onClick={saveInvoice} disabled={iSaving} data-testid="save-invoice-btn">
              {iSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve method dialog */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent data-testid="approve-method-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Approve Invoice</DialogTitle>
            <DialogDescription>
              {approveInv?.vendor_name} · Balance {money(invBalance(approveInv))}
              {(approveInv?.amount_paid || 0) > 0 ? ` (of ${money(approveInv?.amount)} total, ${money(approveInv?.amount_paid)} already paid)` : ""}. How is this being paid?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label>Payment Method</Label>
            <Select value={approveMethod} onValueChange={setApproveMethod}>
              <SelectTrigger data-testid="approve-method-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="check">Approve by Check — a check will be printed</SelectItem>
                <SelectItem value="ach">Approve by ACH — paid electronically, no check to print</SelectItem>
              </SelectContent>
            </Select>

            <div className="space-y-2 rounded-none border bg-muted/30 p-3 !mt-3">
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                <Checkbox checked={approveSplit} onCheckedChange={(v) => { setApproveSplit(!!v); if (v) setApproveAmount(String(invBalance(approveInv))); }} data-testid="approve-split-toggle" />
                Split this payment — {approveMethod === "ach" ? "pay" : "approve"} only part now
              </label>
              {approveSplit && (
                <div className="space-y-1.5">
                  <Label>{approveMethod === "ach" ? "Amount to pay now" : "Amount to approve now"}</Label>
                  <Input type="number" step="0.01" min="0" max={invBalance(approveInv)} value={approveAmount} onChange={(e) => setApproveAmount(e.target.value)} data-testid="approve-amount-input" />
                  {(() => {
                    const amt = parseFloat(approveAmount);
                    const bal = invBalance(approveInv);
                    if (amt > 0 && amt < bal) return <p className="text-xs font-medium text-amber-700 dark:text-amber-400">{money(bal - amt)} balance will stay on Invoices for later approval.</p>;
                    return <p className="text-xs text-muted-foreground">Enter less than {money(bal)} to leave a remaining balance.</p>;
                  })()}
                </div>
              )}
            </div>

            <p className="text-xs text-muted-foreground !mt-3">
              {approveMethod === "ach" ? "The paid portion is marked Paid (ACH). No check is generated." : "Becomes ready for check printing. The check prints for the approved amount; any remainder returns to Pending."}
            </p>
            <div className="space-y-1.5 pt-2">
              <Label>{approveMethod === "ach" ? "Payment date" : "Approval date"}</Label>
              <Input type="date" value={approveDate} onChange={(e) => setApproveDate(e.target.value)} data-testid="approve-date-input" />
              <p className="text-xs text-muted-foreground">Defaults to today. You can change it now or edit it later.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveOpen(false)}>Cancel</Button>
            <Button onClick={doApprove} data-testid="confirm-approve-btn">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AutopayDialog inv={autopayInv} onClose={() => setAutopayInv(null)} onSave={saveAutopay} />

      {/* Disapprove dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid="disapprove-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Disapprove Invoice</DialogTitle>
            <DialogDescription>Add a reason. The invoice moves to the bottom as "Unapproved" and the GM can fix and resubmit it.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label>Reason / comments *</Label>
            <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={4} placeholder="e.g. Wrong amount — should be $842.10, not $482.10. Please correct and resubmit." data-testid="disapprove-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={doReject} data-testid="confirm-disapprove-btn">Disapprove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print check dialog */}
      <Dialog open={printOpen} onOpenChange={setPrintOpen}>
        <DialogContent data-testid="print-check-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Print Check</DialogTitle>
            <DialogDescription>
              {printInv?.vendor_name} · Invoice total {money(printInv?.amount)}
              {(printInv?.amount_paid || 0) > 0 ? ` · Already paid ${money(printInv?.amount_paid)}` : ""}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Check Amount</Label>
              <Input
                type="number" step="0.01" min="0"
                value={printAmount}
                onChange={(e) => setPrintAmount(e.target.value)}
                data-testid="print-check-amount"
              />
              <p className="text-xs text-muted-foreground">Defaults to the amount approved for this check. Any balance left after printing stays on Invoices for later approval.</p>
            </div>
            {bankAccts.accounts.length > 1 && (
              <div className="space-y-1.5">
                <Label>Bank Account</Label>
                <BankAccountSelect accounts={bankAccts.accounts} value={printBankId} testid="print-check-bank"
                  onChange={(id, a) => { setPrintBankId(id); setPrintNumber(String(a?.current_check_number ?? 1001)); }} />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Check Number</Label>
              <Input
                type="number"
                value={printNumber}
                onChange={(e) => setPrintNumber(e.target.value)}
                data-testid="print-check-number"
              />
              <p className="text-xs text-muted-foreground">
                Stock: {property?.bank?.check_stock_type === "preprinted" ? "Pre-printed (data only)" : "Blank (full check drawn)"}. Next number auto-advances after printing.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPrintOpen(false)}>Cancel</Button>
            <Button onClick={doPrintCheck} disabled={printingId === printInv?.id} data-testid="confirm-print-check-btn">
              {printingId === printInv?.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Printer className="mr-2 h-4 w-4" />} Print Check
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Check settings dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent data-testid="check-settings-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Check Printing Settings</DialogTitle>
            <DialogDescription>For {property?.name}. Used to print checks and encode the MICR line.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {bankAccts.accounts.length > 1 && (
              <div className="space-y-1.5">
                <Label>Bank Account</Label>
                <BankAccountSelect accounts={bankAccts.accounts} value={settingsBankId} testid="settings-bank-account"
                  onChange={(id, a) => { setSettingsBankId(id); loadBankForm(a || {}); }} />
                <p className="text-xs text-muted-foreground">Add or remove accounts in Properties → Bank &amp; Checks.</p>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Check Stock</Label>
              <Select value={bankForm.check_stock_type} onValueChange={(v) => setBF("check_stock_type", v)}>
                <SelectTrigger data-testid="stock-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="blank">Blank stock — print the full check (company, bank, routing/account MICR)</SelectItem>
                  <SelectItem value="preprinted">Pre-printed stock — print only date, payee, amount &amp; memo</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Pre-printed: your check paper already has company &amp; bank info; the app writes only the variable fields so you can print and sign.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Company Name (on check)</Label>
                <Input value={bankForm.company_name} onChange={(e) => setBF("company_name", e.target.value)} data-testid="settings-company" />
              </div>
              <div className="space-y-1.5">
                <Label>Bank Name</Label>
                <Input value={bankForm.bank_name} onChange={(e) => setBF("bank_name", e.target.value)} data-testid="settings-bank" />
              </div>
              <div className="space-y-1.5">
                <Label>Routing Number</Label>
                <Input value={bankForm.routing_number} onChange={(e) => setBF("routing_number", e.target.value)} data-testid="settings-routing" />
              </div>
              <div className="space-y-1.5">
                <Label>Account Number</Label>
                <Input value={bankForm.account_number} onChange={(e) => setBF("account_number", e.target.value)} data-testid="settings-account" />
              </div>
              <div className="space-y-1.5">
                <Label>Next Check Number</Label>
                <Input type="number" value={bankForm.current_check_number} onChange={(e) => setBF("current_check_number", e.target.value)} data-testid="settings-check-number" />
              </div>
            </div>
            <div className="rounded-md border border-border bg-muted/30 p-3">
              <p className="text-sm font-medium">Print Alignment</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Nudge everything on the check to line up with pre-printed stock (e.g. VersaCheck Form #1000).
                Values are in <span className="font-medium">inches</span>. If text prints too low, increase the vertical value.
              </p>
              <div className="mt-3 grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Vertical offset (↑ up)</Label>
                  <Input type="number" step="0.05" value={bankForm.print_offset_y}
                    onChange={(e) => setBF("print_offset_y", e.target.value)} data-testid="settings-offset-y" />
                  <p className="text-[11px] text-muted-foreground">Positive moves content up. e.g. <span className="font-medium">0.75</span> to fix printing 0.75″ too low.</p>
                </div>
                <div className="space-y-1.5">
                  <Label>Horizontal offset (→ right)</Label>
                  <Input type="number" step="0.05" value={bankForm.print_offset_x}
                    onChange={(e) => setBF("print_offset_x", e.target.value)} data-testid="settings-offset-x" />
                  <p className="text-[11px] text-muted-foreground">Positive moves content right; negative moves left.</p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>Cancel</Button>
            <Button onClick={saveSettings} disabled={setSaving} data-testid="save-check-settings-btn">
              {setSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt dialog */}
      <Dialog open={rOpen} onOpenChange={setROpen}>
        <DialogContent data-testid="receipt-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{rEditId ? "Edit Receipt" : "Add Credit Card Receipt"}</DialogTitle>
            <DialogDescription>A purchase already paid with the company card. It posts to the P&amp;L by purchase date.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            {(() => { const locked = !!(rEditId && rForm.invoice_id); return (<>
            <div className="col-span-2 space-y-1.5 rounded-lg border border-primary/30 bg-primary/5 p-3" data-testid="receipt-card-block">
              <Label>Credit card used *</Label>
              <div className="flex gap-2">
                <select value={rForm.card_id || ""} onChange={(e) => setRF("card_id", e.target.value)} data-testid="receipt-card"
                  className="flex-1 rounded-md border bg-background px-3 py-2 text-sm">
                  <option value="">— choose card —</option>
                  {(property?.credit_cards || []).map((c) => <option key={c.id} value={c.id}>{c.label} ••{c.last4}</option>)}
                </select>
                <Button type="button" variant="outline" onClick={async () => { const cid = await promptAddCard(); if (cid) setRF("card_id", cid); }} data-testid="receipt-add-card">+ Card</Button>
              </div>
              <p className="text-xs text-muted-foreground">Which card paid for this — so you know what to pay off. You can update this on existing receipts.</p>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Vendor *</Label>
              <Select value={rForm.vendor_id || undefined} onValueChange={onReceiptVendor} disabled={locked}>
                <SelectTrigger data-testid="receipt-vendor"><SelectValue placeholder={vendors.length ? "Select vendor" : "Add a vendor first"} /></SelectTrigger>
                <SelectContent>
                  {vendors.map((v) => (<SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            {!rEditId && rForm.vendor_id && openInvoicesForVendor(rForm.vendor_id).length > 0 && (
              <div className="col-span-2 space-y-1.5">
                <Label>Apply to Invoice (optional)</Label>
                <Select value={rForm.invoice_id || "none"} onValueChange={(v) => {
                  if (v === "none") { setRF("invoice_id", ""); return; }
                  const inv = (invoices || []).find((i) => i.id === v);
                  const bal = inv ? Math.round(((inv.amount || 0) - (inv.amount_paid || 0)) * 100) / 100 : 0;
                  setRForm((f) => ({ ...f, invoice_id: v, amount: String(bal) }));
                }}>
                  <SelectTrigger data-testid="receipt-invoice"><SelectValue placeholder="Not linked" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not linked to an invoice</SelectItem>
                    {openInvoicesForVendor(rForm.vendor_id).map((i) => {
                      const bal = Math.round(((i.amount || 0) - (i.amount_paid || 0)) * 100) / 100;
                      return <SelectItem key={i.id} value={i.id}>{i.invoice_number ? `#${i.invoice_number} · ` : ""}Balance {money(bal)}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Linking applies this receipt as a credit-card payment and reduces the invoice balance.</p>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Purchase Date *</Label>
              <Input type="date" value={rForm.date} onChange={(e) => setRF("date", e.target.value)} disabled={locked} data-testid="receipt-date" />
            </div>
            <div className="space-y-1.5">
              <Label>Amount *</Label>
              <Input type="number" step="0.01" min="0" value={rForm.amount} onChange={(e) => setRF("amount", e.target.value)} placeholder="0.00" disabled={locked} data-testid="receipt-amount" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Category</Label>
              <Select value={rForm.category || "none"} onValueChange={(v) => setRF("category", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="receipt-category"><SelectValue placeholder="Select expense category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Uncategorized</SelectItem>
                  {(ccData?.categories || []).map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.group} · {c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Memo</Label>
              <Textarea value={rForm.memo} onChange={(e) => setRF("memo", e.target.value)} data-testid="receipt-memo" />
            </div>
            {locked && <p className="col-span-2 text-xs text-muted-foreground">Applied to invoice — delete this receipt to change the vendor, amount or date.</p>}
            <div className="col-span-2 space-y-1.5">
              <Label>Receipt Image / PDF {rEditId ? "" : "*"}</Label>
              {rEditId && rForm.__hasFile && !rFile && (
                <p className="text-xs text-muted-foreground">A receipt is already attached. Choose a file to replace it.</p>
              )}
              <Input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setRFile(e.target.files?.[0] || null)} data-testid="receipt-file" />
            </div>
            </>); })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setROpen(false)}>Cancel</Button>
            <Button onClick={saveReceipt} disabled={rSaving || !rForm.vendor_id || (!rEditId && !rFile)} data-testid="save-receipt-btn">
              {rSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {rEditId ? "Update" : "Add Receipt"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pay Credit Card dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-2xl" data-testid="pay-card-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Pay Credit Card from Bank</DialogTitle>
            <DialogDescription>Pick the card you're paying off (or pay across cards), enter the bank payment, then tick receipts until they match it exactly.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Which card are you paying?</Label>
            <div className="flex flex-wrap gap-2" data-testid="pay-card-picker">
              <button type="button" onClick={() => selectCardToPay("")}
                className={`rounded-full border px-3 py-1.5 text-sm ${!payCard ? "border-primary bg-primary/10 font-semibold" : "border-border"}`} data-testid="pay-card-all">
                All cards
              </button>
              {Object.values(unpaidByCard).map((c) => (
                <button type="button" key={c.card_id || "none"} onClick={() => selectCardToPay(c.card_id)}
                  className={`rounded-full border px-3 py-1.5 text-sm ${payCard === c.card_id ? "border-primary bg-primary/10 font-semibold" : "border-border"}`}
                  data-testid={`pay-card-opt-${c.card_id || "none"}`}>
                  {c.card_id ? `${c.label} ••${c.last4}` : "No card set"} <span className="text-muted-foreground">· {money(c.total)} ({c.count})</span>
                </button>
              ))}
            </div>
            {payCard && <p className="text-xs text-muted-foreground">Showing only this card's receipts — all pre-selected and the amount pre-filled. Adjust ticks if you're only paying part.</p>}
          </div>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="space-y-1.5">
              <Label>Payment Date</Label>
              <Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} data-testid="pay-date" />
            </div>
            <div className="space-y-1.5">
              <Label>Bank Payment Amount *</Label>
              <Input type="number" step="0.01" min="0" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} placeholder="0.00" data-testid="pay-amount" />
            </div>
          </div>
          <div className="max-h-72 overflow-auto border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Card</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payVisibleReceipts.map((r) => (
                  <TableRow key={r.id} data-testid={`pay-receipt-${r.id}`} className="cursor-pointer" onClick={() => togglePaySel(r.id)}>
                    <TableCell><Checkbox checked={paySel.includes(r.id)} onCheckedChange={() => togglePaySel(r.id)} data-testid={`pay-check-${r.id}`} /></TableCell>
                    <TableCell className="whitespace-nowrap">{fmtDate(r.date)}</TableCell>
                    <TableCell className="font-medium">{r.vendor_name || r.merchant}</TableCell>
                    <TableCell className="whitespace-nowrap text-xs">{r.card_last4 ? `${r.card_label || "Card"} ••${r.card_last4}` : <span className="text-amber-600">Not set</span>}</TableCell>
                    <TableCell className="text-muted-foreground">{r.category_name || "—"}</TableCell>
                    <TableCell className="text-right font-mono">{money(r.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="flex items-center justify-between border border-border bg-muted/40 px-4 py-3 text-sm">
            <div>
              <span className="text-muted-foreground">Selected:</span> <span className="font-mono font-bold" data-testid="pay-selected-total">{money(paySelTotal)}</span>
              <span className="ml-3 text-muted-foreground">Target:</span> <span className="font-mono">{money(payTarget)}</span>
            </div>
            <div data-testid="pay-diff" className={payMatches ? "font-bold text-[hsl(var(--success))]" : "font-bold text-destructive"}>
              {payMatches ? "✓ Matches exactly" : `Difference: ${money(payDiff)}`}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button>
            <Button onClick={submitPay} disabled={paySaving || !payMatches} data-testid="submit-pay-btn">
              {paySaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Reconcile &amp; Pay
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Blank Checks dialog */}
      <Dialog open={blankOpen} onOpenChange={setBlankOpen}>
        <DialogContent data-testid="blank-checks-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Print Blank Checks</DialogTitle>
            <DialogDescription>
              Prints blank business checks with your bank details, check number and the MICR line at the bottom — the Pay To, date and amount are left empty for QuickBooks (or handwriting) to fill in.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            {bankAccts.accounts.length > 1 && (
              <div className="col-span-2 space-y-1.5">
                <Label>Bank Account</Label>
                <BankAccountSelect accounts={bankAccts.accounts} value={blankBankId} testid="blank-bank"
                  onChange={(id, a) => { setBlankBankId(id); setBlankStart(String(a?.current_check_number ?? 1001)); }} />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Number of checks</Label>
              <Input type="number" min="1" max="100" value={blankCount} onChange={(e) => setBlankCount(e.target.value)} data-testid="blank-count" />
            </div>
            <div className="space-y-1.5">
              <Label>Starting check number</Label>
              <Input type="number" min="1" value={blankStart} onChange={(e) => setBlankStart(e.target.value)} data-testid="blank-start" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Defaults to your next number from Check Settings (currently #{property?.bank?.current_check_number ?? 1001}); edit it to start anywhere. Numbers are sequential and the counter advances so invoice checks won't reuse them. Printed on blank check stock — use MICR magnetic toner for bank-scannable checks.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlankOpen(false)}>Cancel</Button>
            <Button onClick={submitBlank} disabled={blankBusy} data-testid="generate-blank-checks-btn">
              {blankBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Printer className="mr-2 h-4 w-4" />} Generate PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pay Invoice by Credit Card dialog */}
      <Dialog open={ccInvOpen} onOpenChange={setCcInvOpen}>
        <DialogContent data-testid="pay-cc-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Pay Invoice by Credit Card</DialogTitle>
            <DialogDescription>
              {ccInv ? `${ccInv.vendor_name}${ccInv.invoice_number ? ` · #${ccInv.invoice_number}` : ""} — Balance ${money(Math.round(((ccInv.amount || 0) - (ccInv.amount_paid || 0)) * 100) / 100)}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="space-y-1.5">
              <Label>Payment Date</Label>
              <Input type="date" value={ccInvForm.date} onChange={(e) => setCIF("date", e.target.value)} data-testid="cc-inv-date" />
            </div>
            <div className="space-y-1.5">
              <Label>Amount (partial allowed) *</Label>
              <Input type="number" step="0.01" min="0" value={ccInvForm.amount} onChange={(e) => setCIF("amount", e.target.value)} data-testid="cc-inv-amount" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Category</Label>
              <Select value={ccInvForm.category || "none"} onValueChange={(v) => setCIF("category", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="cc-inv-category"><SelectValue placeholder="Select expense category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Uncategorized</SelectItem>
                  {(ccData?.categories || []).map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.group} · {c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Credit card used *</Label>
              <div className="flex gap-2">
                <select value={ccInvForm.card_id} onChange={(e) => setCIF("card_id", e.target.value)} data-testid="cc-inv-card"
                  className="flex-1 rounded-md border bg-background px-3 py-2 text-sm">
                  <option value="">— choose card —</option>
                  {(property?.credit_cards || []).map((c) => <option key={c.id} value={c.id}>{c.label} ••{c.last4}</option>)}
                </select>
                <Button type="button" variant="outline" onClick={async () => { const cid = await promptAddCard(); if (cid) setCIF("card_id", cid); }} data-testid="cc-inv-add-card">+ Card</Button>
              </div>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Memo</Label>
              <Textarea value={ccInvForm.memo} onChange={(e) => setCIF("memo", e.target.value)} data-testid="cc-inv-memo" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Receipt Image / PDF *</Label>
              <Input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setCcInvFile(e.target.files?.[0] || null)} data-testid="cc-inv-file" />
            </div>
            <p className="col-span-2 text-xs text-muted-foreground">
              This records a credit-card payment, reduces the invoice balance (marks it Paid when fully covered), and files a CC receipt you can reconcile later. Pay in multiple installments by repeating with partial amounts.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCcInvOpen(false)}>Cancel</Button>
            <Button onClick={submitCcInv} disabled={ccInvSaving || !ccInvFile} data-testid="submit-cc-inv-btn">
              {ccInvSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Record CC Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>



      {/* File viewer */}
      <Dialog open={!!viewer} onOpenChange={(o) => { if (!o) { if (viewer) URL.revokeObjectURL(viewer.url); setViewer(null); } }}>
        <DialogContent className="max-w-4xl" data-testid="file-viewer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><FileText className="h-5 w-5" /> {viewer?.name}</DialogTitle>
          </DialogHeader>
          {viewer && (viewer.type.includes("pdf") ? (
            <iframe title="invoice" src={viewer.url} className="h-[70vh] w-full border border-border" />
          ) : (
            <img src={viewer.url} alt="invoice" className="max-h-[70vh] w-full object-contain" />
          ))}
        </DialogContent>
      </Dialog>

      {/* Batch check selection */}
      <Dialog open={batchOpen} onOpenChange={setBatchOpen}>
        <DialogContent className="max-w-2xl" data-testid="batch-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><Printer className="h-5 w-5" /> Print checks</DialogTitle>
            <DialogDescription>Choose which approved checks to print. Each gets the next check number for its property.</DialogDescription>
          </DialogHeader>
          {pendingChecks.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No approved checks are waiting to be printed.</p>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <input type="checkbox" className="h-4 w-4" data-testid="batch-select-all"
                    checked={batchSel.length === pendingChecks.length}
                    onChange={(e) => setBatchSel(e.target.checked ? pendingChecks.map((i) => i.id) : [])} />
                  Select all ({pendingChecks.length})
                </label>
                <span className="text-sm text-muted-foreground">{batchSel.length} selected · {money(pendingChecks.filter((i) => batchSel.includes(i.id)).reduce((s, i) => s + i.amount, 0))}</span>
              </div>
              <div className="max-h-[50vh] space-y-3 overflow-auto">
                {[...new Set(pendingChecks.map((i) => i.property_name))].map((pname) => (
                  <div key={pname}>
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <div className="text-xs font-semibold uppercase text-muted-foreground">{pname}</div>
                      {(() => {
                        const first = pendingChecks.find((i) => i.property_name === pname);
                        if (!first || (first.bank_accounts || []).length < 2) return null;
                        return (
                          <select className="h-7 border border-border bg-background px-1 text-xs" value={batchBankIds[first.property_id] || first.default_bank_id}
                            onChange={(e) => setBatchBankIds((m) => ({ ...m, [first.property_id]: e.target.value }))} data-testid={`batch-bank-${first.property_id}`}>
                            {first.bank_accounts.map((a) => <option key={a.id} value={a.id}>{a.nickname} · next #{a.current_check_number}</option>)}
                          </select>
                        );
                      })()}
                    </div>
                    {pendingChecks.filter((i) => i.property_name === pname).map((i) => (
                      <label key={i.id} className="flex items-center gap-3 border-b border-border py-2 text-sm" data-testid={`batch-item-${i.id}`}>
                        <input type="checkbox" className="h-4 w-4" checked={batchSel.includes(i.id)}
                          onChange={() => setBatchSel((s) => s.includes(i.id) ? s.filter((x) => x !== i.id) : [...s, i.id])} />
                        <span className="w-16 font-mono text-xs text-muted-foreground">#{i.check_number}</span>
                        <span className="flex-1 truncate">{i.payee}{i.invoice_number ? <span className="text-muted-foreground"> · {i.invoice_number}</span> : null}</span>
                        <span className="font-mono">{money(i.amount)}</span>
                      </label>
                    ))}
                  </div>
                ))}
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBatchOpen(false)}>Cancel</Button>
            <Button onClick={printSelectedChecks} disabled={batching || batchSel.length === 0} data-testid="batch-print-selected">
              {batching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Printer className="mr-2 h-4 w-4" />} Print {batchSel.length || ""} selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
